/* eslint-disable tailwindcss/no-custom-classname */
import { SingSaverRewardsSectionId } from '@hyphengroup/cms-components/cms-blocks/ComparisonTable/constants';
import { useProductBlockAsideModal } from '@hyphengroup/cms-components/components/ProductBlockAsideModal';
import { RewardThumbnail } from '@hyphengroup/cms-components/components/RewardThumbnail';
import useOpenRewardModal from '@hyphengroup/cms-components/hooks/useOpenRewardModal';
import { formatData } from '@hyphengroup/cms-components/utils/helpers/formatDate';
import { currencyFormatterNoDecimal } from '@hyphengroup/cms-components/utils/number';
import classNames from 'classnames';
import { useLocaleSpecificClient } from 'hooks/useSpecificClient';
import { Fragment, useCallback, useMemo } from 'react';
import { staticText } from 'utils/staticText';
import { FeCarousel } from '../../components-internal/FeCarousel';
import { getUrgencyPhase, tr } from '../../helpers';
import { currencyFormat } from '../../helpers/formatters';
import { CONTENT_MHG } from '../../translations/mhg';
import { PeriodGift, VerticalType } from '../../types';
import { CmsProduct } from '$/(cms)/_modules/cmsService/types';
import { useProductDetailModal } from '$/components/cms/helpers/useProductDetailModal';
import { useClientContext } from '$/modules/context/client';

const translations = staticText({
  apply: {
    _: 'Apply by :formattedDate to claim your reward',
    'zh-hk': ':formattedDate前申請可享以下優惠:',
    'zh-tw': '於 :formattedDate 前申辦及達指定門檻享以下優惠：',
  },
});

export const ProductCampaignGifts = ({
  gifts,
  untilDate,
  singleProduct,
  vertical,
  blockIndex,
}: {
  gifts?: PeriodGift[];
  untilDate: string;
  singleProduct?: CmsProduct | null;
  vertical?: VerticalType;
  blockIndex?: number;
}) => {
  /// Parse the ISO string into a Date object
  const text = useLocaleSpecificClient(translations.apply);

  const { lang } = useClientContext();
  const staticText = CONTENT_MHG.get(lang);
  const { closeModal: closeProductAside } = useProductBlockAsideModal();
  const { openModal: openRewardModal } = useOpenRewardModal();
  const rewardModalComponentKey = `Campaign_${blockIndex ?? 'undefined'}`;
  // "_pdp" keeps the key distinct from the sibling ProductCampaignParticipatingCards'
  // RewardAsideModal, which shares the same rewardModal hash (CMS-1924).
  const { openProductDetail, productDetailModal } = useProductDetailModal({
    componentUniqueKey: `CampaignGifts_${blockIndex ?? 'undefined'}_pdp`,
  });
  const formattedDate = formatData(untilDate, 'MMM dd, yyyy', lang);

  const handleGiftClick = useCallback(() => {
    if (!singleProduct) {
      return;
    }

    if (
      vertical === 'PERSONAL_LOANS' &&
      (singleProduct.welcome_offers?.length ||
        singleProduct.exclusive_offers?.length)
    ) {
      openRewardModal(singleProduct.uid ?? '', rewardModalComponentKey);
      return;
    }

    if (singleProduct.url_alias) {
      openProductDetail(singleProduct, SingSaverRewardsSectionId);
    } else {
      closeProductAside();
    }
  }, [
    closeProductAside,
    openProductDetail,
    openRewardModal,
    rewardModalComponentKey,
    singleProduct,
    vertical,
  ]);

  const isSingleProductCampaign = Boolean(singleProduct);

  const gifts_ = useMemo(() => {
    return gifts ?? [];
  }, [gifts]);

  const Gifts = useMemo(() => {
    return gifts_.map((gift__, index) => {
      if (!gift__) {
        return null;
      }

      const gift = gift__;
      if (!gift) {
        return null;
      }
      const giftValue = gift.gift_value;

      if (!giftValue) {
        return null;
      }
      const subtitle =
        giftValue?.topup_label && giftValue?.topup_amount
          ? `${giftValue.topup_label} ${currencyFormat(giftValue.prefix!)}${currencyFormatterNoDecimal.format(giftValue.topup_amount)}`
          : '';

      const title = giftValue.price
        ? `${staticText.worth} ${currencyFormat(giftValue.prefix!)}${currencyFormatterNoDecimal.format(giftValue.price ?? 0)}`
        : '';
      const key = gift.gift_title ?? gift__.uid ?? `gift-${index}`;
      const content = (
        <RewardThumbnail
          data-gift-uid={gift.uid}
          tooltip={gift.gift_title}
          title={title}
          subtitle={subtitle}
          image={gift.mobile_image}
          // eslint-disable-next-line tailwindcss/no-arbitrary-value
          rewardThumbnailSizeClasses={classNames(
            'h-32 w-[105px] md:h-36 md:w-[138px] xl:h-36 xl:w-[141px]',
          )}
          tagText={(() => {
            const urgencyLabel = giftValue?.urgency_label;

            if (urgencyLabel) return urgencyLabel;

            return getUrgencyPhase(lang, gift.startDate, gift.endDate)?.value;
          })()}
          urgentTagText={!!giftValue?.urgency_label}
        />
      );

      if (!isSingleProductCampaign) {
        return <Fragment key={key}>{content}</Fragment>;
      }

      return (
        <button
          key={key}
          type="button"
          onClick={handleGiftClick}
          className="text-left"
          aria-label={gift.gift_title || title || 'View rewards'}
        >
          {content}
        </button>
      );
    });
  }, [
    gifts_,
    handleGiftClick,
    isSingleProductCampaign,
    staticText.worth,
    lang,
  ]);
  return (
    <div>
      <p className="mhg-alpha-typography-bullet-2 md:mhg-alpha-typography-bullet-1 mt-padding/y-md mb-0 text-colors/neutral-gray-900 lg:mb-spacing/padding/soft-8px">
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        {tr(text ?? ('' as any), lang, {
          formattedDate: formattedDate ?? '',
        })}
      </p>
      <div className="hidden gap-spacing/gap/soft-8px lg:grid lg:grid-cols-3">
        {Gifts}
      </div>
      <FeCarousel
        swiperPropsOverride={{
          pagination: true,
          centeredSlides: false,
          slidesPerView: 'auto',
          spaceBetween: 6,
          breakpoints: {
            768: {
              spaceBetween: 8,
            },
          },
        }}
        swiperNestedClassNames="!h-[142px] md:!h-[160px]"
        swiperWrapperClassNames="items-start"
        swiperClassNames="lg:hidden w-full relative mt-padding/y-sm"
        slideClassNames="flex !h-auto !w-auto flex-col"
        navigationWrapperClassNames="hidden 1280:block"
      >
        {Gifts}
      </FeCarousel>
      {productDetailModal}
    </div>
  );
};
